import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  IconButton,
  Flex,
  Button,
  keyframes,
} from '@chakra-ui/react'
import { ChatIcon, ChevronDownIcon } from '@chakra-ui/icons'
import { LuSquare, LuSparkles } from 'react-icons/lu'
import { chatWithArticles, ChatMessage } from '../../../../api/medinsight_llm'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'

// Animation de typing indicator
const typing = keyframes`
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
`

// Animation pour le gradient
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

interface ProjectChatInterfaceProps {
  projectId: string
  projectName?: string
  sourceCount?: number
}

export const ProjectChatInterface: React.FC<ProjectChatInterfaceProps> = ({
  projectId,
  projectName,
  sourceCount,
}) => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const streamingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const storageKey = `aletheia_chat_project_${projectId}`

  // Check if messages contain ALMA/Blot context that doesn't belong in project chat
  const isInvalidProjectHistory = (msgs: ChatMessage[]): boolean => {
    const invalidPatterns = [
      'Blots detected',
      'Bands counted',
      'Processing time',
      'blots détectés',
      'bandes comptées',
      'Parasitemia',
      'Parasitémie',
      'cellules infectées',
      'infected cells',
      'LDBIO',
      'Western Blot Analysis',
      'Analyse Western Blot',
    ]

    return msgs.some(msg =>
      invalidPatterns.some(pattern =>
        msg.content.toLowerCase().includes(pattern.toLowerCase())
      )
    )
  }

  // Charger l'historique depuis localStorage
  useEffect(() => {
    try {
      const savedMessages = localStorage.getItem(storageKey)
      if (savedMessages) {
        const parsedMessages = JSON.parse(savedMessages)
        // Clear history if it contains ALMA/Blot content (wrong context)
        if (isInvalidProjectHistory(parsedMessages)) {
          console.log('Clearing invalid project chat history (contains ALMA/Blot content)')
          localStorage.removeItem(storageKey)
          setMessages([])
        } else {
          setMessages(parsedMessages)
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error)
    }
  }, [storageKey])

  // Sauvegarder l'historique dans localStorage
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(messages))
      } catch (error) {
        console.error('Failed to save chat history:', error)
      }
    }
  }, [messages, storageKey])

  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current)
      }
    }
  }, [])

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' })
  }

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
    const hasMessages = container.children.length > 0
    setShowScrollButton(!isAtBottom && hasMessages)
  }, [])

  useEffect(() => {
    if (isLoading || isStreaming) {
      scrollToBottom()
    } else if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
      if (isAtBottom) {
        scrollToBottom()
      }
    }
    setTimeout(() => handleScroll(), 50)
  }, [messages, isLoading, isStreaming, handleScroll])

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current)
      streamingIntervalRef.current = null
    }

    setIsLoading(false)
    setIsStreaming(false)

    const stoppedMessage: ChatMessage = {
      role: 'assistant',
      content: t('aletheia.stoppedByUser'),
    }
    setMessages((prev) => {
      const lastMsg = prev[prev.length - 1]
      if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
        return [...prev.slice(0, -1), stoppedMessage]
      }
      return [...prev, stoppedMessage]
    })
  }, [])

  const streamAssistantMessage = async (text: string) => {
    const answer = text || ''

    setIsStreaming(true)
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

    await new Promise<void>((resolve) => {
      let index = 0
      const step = () => {
        const currentChar = answer[index]
        const charsToAdd = currentChar === ' ' ? 3 : 1
        index += charsToAdd

        setMessages((prev) => {
          if (!prev.length) return prev
          const updated = [...prev]
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: answer.slice(0, index),
          }
          return updated
        })

        if (index >= answer.length) {
          streamingIntervalRef.current && clearInterval(streamingIntervalRef.current)
          streamingIntervalRef.current = null
          setIsStreaming(false)
          resolve()
          return
        }
      }

      streamingIntervalRef.current = setInterval(step, 30)
    })
  }

  const handleSend = async () => {
    if (!input.trim() || isLoading || isStreaming) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const response = await chatWithArticles(
        [...messages, userMessage],
        projectId,
        abortController.signal
      )

      await streamAssistantMessage(response.answer)
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return
      }

      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: error.message
          ? t('aletheia.errorMessage', { message: error.message })
          : t('aletheia.errorDefault'),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const suggestedQuestions = [
    t('aletheia.suggestions.project.summarize'),
    t('aletheia.suggestions.project.commonPoints'),
    t('aletheia.suggestions.project.gaps'),
    t('aletheia.suggestions.project.compareMethods'),
  ]

  return (
    <VStack spacing={0} h="full" align="stretch" position="relative">
      {/* Messages Area - Wrapper with position relative for absolute button */}
      <Box
        flex={1}
        position="relative"
        overflow="hidden"
        bg="gray.50"
        minH={0}
      >
        <Box
          ref={messagesContainerRef}
          onScroll={handleScroll}
          overflowY="auto"
          h="100%"
          w="100%"
        >
        <VStack spacing={4} p={6} align="stretch">
          {messages.length === 0 ? (
            <VStack spacing={6} py={12} px={6}>
              {/* Logo avec animation */}
              <Box
                position="relative"
                width="80px"
                height="80px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  position="absolute"
                  width="100%"
                  height="100%"
                  bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
                  borderRadius="full"
                  opacity={0.15}
                  animation={`${gradientAnimation} 8s ease infinite`}
                  backgroundSize="200% 200%"
                />
                <Box
                  bg="rgba(255, 255, 255, 0.9)"
                  backdropFilter="blur(10px)"
                  borderRadius="full"
                  width="64px"
                  height="64px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 32px rgba(128, 90, 213, 0.2)"
                  border="2px solid"
                  borderColor="rgba(128, 90, 213, 0.1)"
                >
                  <Text
                    fontSize="4xl"
                    fontWeight="bold"
                    fontFamily="Georgia, serif"
                    bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
                    bgClip="text"
                  >
                    Λ
                  </Text>
                </Box>
              </Box>

              {/* Titre et description */}
              <VStack spacing={2}>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  textAlign="center"
                  fontFamily="Space Grotesk, sans-serif"
                  bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
                  bgClip="text"
                >
                  {t('aletheia.greeting.project')}
                </Text>
                <Text fontSize="sm" color="gray.600" textAlign="center" maxW="md">
                  {t('aletheia.description.project')}
                </Text>
              </VStack>

              {/* Info Badge */}
              <HStack
                spacing={2}
                bg="purple.50"
                px={4}
                py={2}
                borderRadius="full"
                border="1px solid"
                borderColor="purple.200"
              >
                <LuSparkles size={16} color="#805ad5" />
                <Text fontSize="xs" fontWeight="semibold" color="purple.700">
                  {t('aletheia.context.sourcesAvailable', { count: sourceCount || 0 })}
                </Text>
              </HStack>

              {/* Questions suggérées */}
              <VStack spacing={2} w="full" maxW="lg" mt={2}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
                  💡 {t('aletheia.suggestedQuestions')}
                </Text>
                {suggestedQuestions.map((q, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant="outline"
                    onClick={() => setInput(q)}
                    w="full"
                    justifyContent="flex-start"
                    textAlign="left"
                    whiteSpace="normal"
                    height="auto"
                    py={3}
                    px={4}
                    borderColor="gray.200"
                    _hover={{
                      borderColor: 'purple.400',
                      bg: 'purple.50',
                      transform: 'translateX(4px)',
                    }}
                    transition="all 0.2s"
                    leftIcon={<Text fontSize="xs">→</Text>}
                  >
                    <Text fontSize="sm">{q}</Text>
                  </Button>
                ))}
              </VStack>
            </VStack>
          ) : (
            messages.map((msg, idx) => (
              <Flex
                key={idx}
                justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                w="full"
              >
                <Box
                  maxW="80%"
                  bg={msg.role === 'user' ? undefined : 'white'}
                  borderRadius="lg"
                  p={4}
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor={msg.role === 'user' ? 'purple.200' : 'gray.200'}
                  bgGradient={
                    msg.role === 'user'
                      ? 'linear(135deg, purple.500 0%, pink.400 100%)'
                      : undefined
                  }
                  color={msg.role === 'user' ? 'white' : 'gray.800'}
                >
                  {msg.role === 'assistant' ? (
                    <Box
                      className="markdown-content"
                      sx={{
                        '& p': { mb: 2 },
                        '& ul, & ol': { ml: 4, mb: 2 },
                        '& li': { mb: 1 },
                        '& strong': { fontWeight: 'bold' },
                        '& code': {
                          bg: 'gray.100',
                          px: 1,
                          py: 0.5,
                          borderRadius: 'sm',
                          fontSize: 'sm',
                        },
                      }}
                    >
                      <ReactMarkdown>{msg.content || '...'}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Text whiteSpace="pre-wrap">{msg.content}</Text>
                  )}
                </Box>
              </Flex>
            ))
          )}

          {isLoading && !isStreaming && (
            <Flex justify="flex-start" w="full">
              <HStack spacing={2} bg="white" borderRadius="lg" p={4} boxShadow="sm">
                <Box
                  as="span"
                  display="inline-block"
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg="purple.500"
                  animation={`${typing} 1.4s infinite`}
                  sx={{ animationDelay: '0s' }}
                />
                <Box
                  as="span"
                  display="inline-block"
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg="purple.500"
                  animation={`${typing} 1.4s infinite`}
                  sx={{ animationDelay: '0.2s' }}
                />
                <Box
                  as="span"
                  display="inline-block"
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg="purple.500"
                  animation={`${typing} 1.4s infinite`}
                  sx={{ animationDelay: '0.4s' }}
                />
              </HStack>
            </Flex>
          )}

          <div ref={messagesEndRef} />
        </VStack>
        </Box>

        {/* Scroll to bottom button - Fixed position */}
        {showScrollButton && (
          <IconButton
            aria-label={t('aletheia.scrollToBottom')}
            icon={<ChevronDownIcon />}
            position="absolute"
            bottom="20px"
            right="24px"
            borderRadius="full"
            size="lg"
            bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
            color="white"
            onClick={() => scrollToBottom()}
            boxShadow="0 8px 20px rgba(128, 90, 213, 0.5)"
            _hover={{
              boxShadow: '0 12px 28px rgba(128, 90, 213, 0.7)',
              transform: 'scale(1.1)',
            }}
            _active={{
              transform: 'scale(0.95)',
            }}
            zIndex={10}
            transition="all 0.2s"
          />
        )}
      </Box>

      {/* Input Area */}
      <Box
        px={6}
        py={4}
        borderTopWidth="1px"
        borderColor="gray.200"
        bg="white"
        flexShrink={0}
      >
        <HStack spacing={2} align="flex-end">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aletheia.placeholder')}
            size="md"
            resize="none"
            rows={2}
            minH="60px"
            bg="gray.50"
            borderColor="gray.300"
            _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px #805ad5' }}
          />
          <IconButton
            aria-label={isLoading || isStreaming ? t('aletheia.stopGeneration') : t('aletheia.sendMessage')}
            icon={isLoading || isStreaming ? <LuSquare size={20} /> : <ChatIcon />}
            bgGradient={
              isLoading || isStreaming
                ? 'linear(to-r, red.500, red.600)'
                : 'linear(135deg, purple.500 0%, pink.400 100%)'
            }
            color="white"
            size="lg"
            onClick={isLoading || isStreaming ? handleStop : handleSend}
            isDisabled={!input.trim() && !(isLoading || isStreaming)}
            _hover={{
              transform: 'scale(1.05)',
            }}
            transition="all 0.2s"
          />
        </HStack>
      </Box>
    </VStack>
  )
}
